import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { cleanupRemovedCloudinaryAssets } from './adminApi';
import { db } from './firebase';
import { buildMediaSnapshot } from './media';

function withoutUndefined(value) {
  if (Array.isArray(value)) return value.map(withoutUndefined);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, withoutUndefined(item)]),
    );
  }
  return value;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getNextSequentialDocumentId(recordsOrIds, prefix) {
  const cleanPrefix = String(prefix || '').trim();
  if (!cleanPrefix) throw new Error('Document ID prefix configured nathi.');

  const pattern = new RegExp(`^${escapeRegExp(cleanPrefix)}(\\d+)$`, 'i');
  const highestNumber = (recordsOrIds || []).reduce((highest, item) => {
    const id = typeof item === 'string' ? item : item?.id;
    const match = String(id || '').match(pattern);
    if (!match) return highest;
    return Math.max(highest, Number(match[1]));
  }, 0);

  return `${cleanPrefix}${highestNumber + 1}`;
}

export async function getNextAvailableDocumentId(collectionName, prefix) {
  const snapshot = await getDocs(collection(db, collectionName));
  return getNextSequentialDocumentId(snapshot.docs.map((item) => item.id), prefix);
}

export function subscribeToCollection(collectionName, onData, onError) {
  const collectionRef = collection(db, collectionName);
  return onSnapshot(collectionRef, (snapshot) => {
    const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    rows.sort((a, b) => {
      const aOrder = Number.isFinite(a.sortOrder) ? a.sortOrder : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(b.sortOrder) ? b.sortOrder : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || String(a.id).localeCompare(String(b.id), 'en', { numeric: true });
    });
    onData(rows);
  }, onError);
}

export function subscribeToDocument(collectionName, documentId, onData, onError) {
  return onSnapshot(doc(db, collectionName, documentId), (snapshot) => {
    onData(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
  }, onError);
}

export async function saveRecord(collectionName, record, existing = false) {
  const suppliedId = String(record.id || '').trim();
  const reference = suppliedId ? doc(db, collectionName, suppliedId) : doc(collection(db, collectionName));
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...editable } = record;
  const payload = withoutUndefined({
    ...editable,
    isActive: editable.isActive !== false,
    updatedAt: serverTimestamp(),
    ...(!existing && { createdAt: serverTimestamp() }),
  });

  if (existing) {
    await setDoc(reference, payload, { merge: true });
  } else {
    await runTransaction(db, async (transaction) => {
      const current = await transaction.get(reference);
      if (current.exists()) {
        const duplicateError = new Error(`Document ID ${reference.id} already use ma chhe.`);
        duplicateError.code = 'already-exists';
        throw duplicateError;
      }
      transaction.set(reference, payload);
    });
  }

  return reference.id;
}

export async function updateRecord(collectionName, documentId, changes) {
  await updateDoc(doc(db, collectionName, documentId), withoutUndefined({
    ...changes,
    updatedAt: serverTimestamp(),
  }));
}

export async function permanentlyDeleteRecord(collectionName, documentId, record) {
  await deleteDoc(doc(db, collectionName, documentId));
  await cleanupRemovedCloudinaryAssets({
    collectionName,
    documentId,
    beforeMedia: buildMediaSnapshot(record),
    afterMedia: null,
  }).catch(() => undefined);
}

export async function reorderRecords(collectionName, records) {
  const batch = writeBatch(db);
  records.forEach((record, index) => {
    batch.update(doc(db, collectionName, record.id), {
      sortOrder: index + 1,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function getCollectionCount(collectionName) {
  const snapshot = await getDocs(query(collection(db, collectionName), orderBy('sortOrder'))).catch(
    () => getDocs(collection(db, collectionName)),
  );
  return snapshot.size;
}
